import type { Experiment } from 'types';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { config } from 'config';

/**
 * Daily sessions, conversions and revenue per variant.
 *
 * Fetched separately from the main stats blob rather than folded into it: the blob is
 * a pre-computed cache regenerated by a cron, while this is a live query, and the
 * charts built on it are below the fold. Keeping them apart means the page's first
 * paint does not wait on a query most visits never scroll to.
 */
export type TimeseriesPoint = {
  date: string;
  variantId: number;
  sessions: number;
  conversions: number;
  revenue: number;
};

export type TimeseriesResponse = {
  points: TimeseriesPoint[];
  /**
   * First day session counts exist for, or null. The hourly rollup only starts when
   * it shipped and the rows it derives from are deleted after processing, so an
   * experiment that ended earlier has conversions but no sessions, and therefore no
   * rate. The UI needs to say that rather than draw an empty chart.
   */
  sessionsFrom: string | null;
  variants: { id: number; name: string; isControl: boolean }[];
};

export const useExperimentTimeseries = (experiment: Experiment, currency: string | null) => {
  const statisticsClient = useClient(config.STATISTICS_URL);

  const { data, isLoading, error } = useQuery<TimeseriesResponse | null>({
    queryKey: ['EXPERIMENT_TIMESERIES', experiment.id, currency],
    queryFn: () => statisticsClient.get(`/${experiment.id}/timeseries?currency=${currency ?? 'nill'}`),
    enabled: !!experiment?.id && !!currency,
    staleTime: 1000 * 60,
  });

  return {
    points: data?.points ?? [],
    sessionsFrom: data?.sessionsFrom ?? null,
    variants: data?.variants ?? [],
    isLoading,
    error,
  };
};
